import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { CheckCircle2, Home, Loader2, XCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Success = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [orderDetails, setOrderDetails] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const verifyPayment = async () => {
      if (!orderId) {
        setStatus('error');
        setError('Order ID not found');
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/eod/verify-payment/${orderId}`);
        
        if (response.data.success) {
          setOrderDetails(response.data.order);
          setStatus('success');
        } else {
          setStatus('error');
          setError('Payment verification failed');
        }
      } catch (err) {
        console.error('Payment verification error:', err);
        setStatus('error');
        setError(err.response?.data?.detail || 'Failed to verify payment');
      }
    };

    verifyPayment();
  }, [orderId]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50 flex items-center justify-center">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="pt-6 text-center">
            <Loader2 className="w-16 h-16 text-red-600 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-800">Verifying Payment...</h2>
            <p className="text-slate-600 mt-2">Please wait while we confirm your order</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50 flex items-center justify-center">
        <Card className="w-full max-w-md mx-4 border-red-200">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <CardTitle className="text-2xl text-red-700">Payment Failed</CardTitle>
            <CardDescription className="text-red-600">{error}</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button
              onClick={() => navigate('/eod')}
              className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white"
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50">
      <div className="h-20"></div>
      <div className="max-w-2xl mx-auto px-6 py-12">
        <Card className="shadow-2xl border-green-200">
          <CardHeader className="text-center">
            <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="w-12 h-12 text-green-600" />
            </div>
            <CardTitle className="text-3xl text-green-700">Order Confirmed! ✅</CardTitle>
            <CardDescription className="text-lg text-slate-600 mt-2">
              Thank you for choosing KISAT Media
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Order Summary */}
            <div className="bg-slate-50 rounded-lg p-6 space-y-4">
              <h3 className="text-xl font-semibold text-slate-800 border-b border-slate-300 pb-2">
                Order Summary
              </h3>
              
              <div className="grid grid-cols-2 gap-y-3 text-sm">
                <div className="text-slate-600">Order ID:</div>
                <div className="font-mono font-semibold text-slate-800 text-right">{orderDetails?.orderId}</div>
                
                <div className="text-slate-600">Name:</div>
                <div className="font-semibold text-slate-800 text-right">{orderDetails?.name}</div>
                
                <div className="text-slate-600">Phone:</div>
                <div className="font-semibold text-slate-800 text-right">{orderDetails?.phone}</div>
                
                <div className="text-slate-600">Plan:</div>
                <div className="font-semibold text-slate-800 text-right">{orderDetails?.plan}</div>
                
                <div className="text-slate-600 border-t border-slate-300 pt-3">Amount Paid:</div>
                <div className="font-bold text-green-600 text-lg text-right border-t border-slate-300 pt-3">
                  ₹{orderDetails?.amount}
                </div>
              </div>

              {orderDetails?.instructions && (
                <div className="pt-3 border-t border-slate-300">
                  <div className="text-slate-600 text-sm mb-2">Special Instructions:</div>
                  <div className="text-slate-800 text-sm bg-white p-3 rounded border border-slate-200">
                    {orderDetails.instructions}
                  </div>
                </div>
              )}
            </div>

            {/* Next Steps */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">📧 What's Next?</h4>
              <p className="text-sm text-blue-800">
                Our team will contact you within 24 hours to discuss your project details and timeline.
                Check your email for order confirmation.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button
                onClick={() => navigate('/')}
                className="flex-1 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white"
              >
                <Home className="mr-2 h-4 w-4" />
                Back to Home
              </Button>
              <Button
                onClick={() => navigate('/eod')}
                variant="outline"
                className="flex-1 border-red-600 text-red-600 hover:bg-red-50"
              >
                Place Another Order
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Success;